import React from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';

// Prepares (idempotently) an unconfirmed TOTP credential for the logged-in
// Identity user and returns the QR + manual secret. Field name is camelized
// (graphql-ruby default); the payload fields are camelize:false so they stay
// snake_case to match the server type.
const MFA_SETUP = gql`
  mutation mfaEnrollmentSetup {
    mfaEnrollmentSetup {
      otp_secret
      qr_svg
      provisioning_uri
    }
  }
`;

const MFA_CONFIRM = gql`
  mutation confirmMfaEnrollment($code: String!) {
    confirmMfaEnrollment(totp_code: $code) {
      success
      backup_codes
      error
      locked
      locked_until
    }
  }
`;

const TRANSLATIONS = {
  en: {
    title: 'Set up two-factor authentication',
    subtitle:
      'Scan the QR code with your authenticator app, or enter the setup key manually. Then enter the 6-digit code to confirm.',
    manual_entry: "Can't scan? Enter this setup key manually:",
    copy_secret: 'Copy setup key',
    code_label: '6-digit code',
    code_hint: 'Enter the code displayed in your authenticator app',
    verify: 'Verify and continue',
    verifying: 'Verifying…',
    invalid: "That code didn't match. Please try again.",
    locked_title: 'Too many attempts',
    locked_body: 'Too many incorrect codes were entered. Please wait and try again.',
    locked_until: 'You can try again after',
    setup_error: "We couldn't start MFA setup. Please refresh the page or contact support.",
    backup_title: 'Save your backup codes',
    backup_body:
      'These one-time backup codes let you sign in if you lose your authenticator. Save them somewhere safe — they will not be shown again.',
    secure_note: 'Store these codes in a secure location. Each code can only be used once.',
    backup_ack: 'I have saved these backup codes in a secure location',
    finish: 'Continue',
    copy: 'Copy codes',
    copied: 'Copied',
    download: 'Download codes',
  },
  fr: {
    title: 'Configurer l’authentification à deux facteurs',
    subtitle:
      "Scannez le code QR avec votre application d'authentification, ou saisissez la clé manuellement. Saisissez ensuite le code à 6 chiffres pour confirmer.",
    manual_entry: 'Impossible de scanner ? Saisissez cette clé manuellement :',
    copy_secret: 'Copier la clé',
    code_label: 'Code à 6 chiffres',
    code_hint: "Saisissez le code affiché dans votre application d'authentification",
    verify: 'Vérifier et continuer',
    verifying: 'Vérification…',
    invalid: 'Ce code ne correspond pas. Veuillez réessayer.',
    locked_title: 'Trop de tentatives',
    locked_body: 'Trop de codes incorrects ont été saisis. Veuillez patienter et réessayer.',
    locked_until: 'Vous pourrez réessayer après',
    setup_error: "Impossible de démarrer la configuration MFA. Actualisez la page ou contactez le support.",
    backup_title: 'Enregistrez vos codes de secours',
    backup_body:
      "Ces codes de secours à usage unique vous permettent de vous connecter si vous perdez l'accès à votre authentificateur. Conservez-les en lieu sûr — ils ne seront plus affichés.",
    secure_note: 'Conservez ces codes en lieu sûr. Chaque code ne peut être utilisé qu’une seule fois.',
    backup_ack: 'J’ai enregistré ces codes de secours en lieu sûr',
    finish: 'Continuer',
    copy: 'Copier les codes',
    copied: 'Copié',
    download: 'Télécharger',
  },
};

// Shared button class strings (succession token palette).
const PRIMARY_BTN =
  'mt-6 w-full inline-flex justify-center items-center rounded-lg bg-succession-primary px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-succession-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-succession-primary';
const PRIMARY_BTN_DISABLED =
  'mt-6 w-full inline-flex justify-center items-center rounded-lg bg-succession-primary opacity-60 cursor-not-allowed px-4 py-2.5 text-sm font-medium text-white shadow-sm';

class MfaEnrollmentStep extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      phase: 'loading', // loading | pairing | backup_codes | locked | error
      otpSecret: null,
      qrSvg: null,
      totpCode: '',
      backupCodes: [],
      error: null,
      lockedUntil: null,
      isSubmitting: false,
      copied: false,
      secretCopied: false,
      acknowledged: false,
    };
    this.otpRefs = [];
    this.handleVerify = this.handleVerify.bind(this);
    this.handleFinish = this.handleFinish.bind(this);
    this.handleCopy = this.handleCopy.bind(this);
    this.handleCopySecret = this.handleCopySecret.bind(this);
    this.handleOtpPaste = this.handleOtpPaste.bind(this);
  }

  componentDidMount() {
    this.startSetup();
  }

  componentDidUpdate(_prevProps, prevState) {
    // Focus the first OTP box as soon as the pairing form appears. preventScroll
    // stops the browser from scrolling the (often below-the-fold) box into view,
    // which would otherwise jump the onboarding step to the bottom after the
    // async setup mutation resolves — defeating the wizard's scroll-to-top.
    // Defer to the next tick so focus runs after any same-commit scrollTo the
    // wizard performs, making the ordering robust rather than lifecycle-order
    // dependent. Guarded against unmount since it now runs asynchronously.
    if (prevState.phase !== 'pairing' && this.state.phase === 'pairing' && this.otpRefs[0]) {
      this._focusTimer = setTimeout(() => {
        if (!this._unmounted && this.otpRefs[0]) {
          this.otpRefs[0].focus({ preventScroll: true });
        }
      }, 0);
    }
  }

  componentWillUnmount() {
    // Guard async mutation callbacks + clear the copy "Copied" timers and the
    // deferred-focus timer so they don't fire on an unmounted component when the
    // wizard advances.
    this._unmounted = true;
    clearTimeout(this._copyTimer);
    clearTimeout(this._secretTimer);
    clearTimeout(this._focusTimer);
  }

  t(key) {
    const lang = this.props.currentLanguage === 'fr' ? 'fr' : 'en';
    return TRANSLATIONS[lang][key] || TRANSLATIONS.en[key] || key;
  }

  startSetup() {
    const { client } = this.props;
    if (!client) {
      this.setState({ phase: 'error' });
      return;
    }
    client
      .mutate({ mutation: MFA_SETUP })
      .then(({ data }) => {
        if (this._unmounted) return;
        const setup = data && data.mfaEnrollmentSetup;
        if (!setup) {
          // No payload means enrollment doesn't apply (already enrolled, or
          // flag/provider mismatch). Let the wizard move on; the server-side
          // backstop is the real gate.
          this.props.onNext && this.props.onNext();
          return;
        }
        this.setState({
          phase: 'pairing',
          otpSecret: setup.otp_secret,
          qrSvg: setup.qr_svg,
        });
      })
      .catch((e) => {
        if (this._unmounted) return;
        console.error('MFA setup failed:', e);
        this.setState({ phase: 'error', error: this.t('setup_error') });
      });
  }

  // --- OTP box handlers ------------------------------------------------------
  handleOtpChange(i, value) {
    const digit = value.replace(/\D/g, '').slice(-1);
    const arr = this.state.totpCode.split('');
    arr[i] = digit;
    const next = arr.join('').slice(0, 6);
    this.setState({ totpCode: next, error: null });
    if (digit && this.otpRefs[i + 1]) this.otpRefs[i + 1].focus();
  }

  handleOtpKeyDown(i, e) {
    if (e.key === 'Backspace' && !this.state.totpCode[i] && this.otpRefs[i - 1]) {
      this.otpRefs[i - 1].focus();
    }
  }

  handleOtpPaste(e) {
    e.preventDefault();
    const digits = (e.clipboardData || window.clipboardData)
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, 6);
    this.setState({ totpCode: digits });
    const idx = Math.min(digits.length, 5);
    if (this.otpRefs[idx]) this.otpRefs[idx].focus();
  }

  handleVerify(e) {
    e.preventDefault();
    const code = (this.state.totpCode || '').trim();
    if (code.length < 6 || this.state.isSubmitting) return;

    this.setState({ isSubmitting: true, error: null });
    this.props.client
      .mutate({ mutation: MFA_CONFIRM, variables: { code } })
      .then(({ data }) => {
        if (this._unmounted) return;
        const result = data && data.confirmMfaEnrollment;
        if (result && result.success) {
          const codes = result.backup_codes || [];
          if (codes.length > 0) {
            this.setState({ phase: 'backup_codes', backupCodes: codes, isSubmitting: false });
          } else {
            // Already enrolled / codes previously issued — nothing to show.
            // Clear isSubmitting first so the button never stays frozen if
            // onNext doesn't unmount this step.
            this.setState({ isSubmitting: false }, () => {
              this.props.onNext && this.props.onNext();
            });
          }
          return;
        }
        if (result && result.locked) {
          this.setState({ phase: 'locked', lockedUntil: result.locked_until, isSubmitting: false });
          return;
        }
        this.setState({
          isSubmitting: false,
          totpCode: '',
          error: (result && result.error) || this.t('invalid'),
        });
        if (this.otpRefs[0]) this.otpRefs[0].focus({ preventScroll: true });
      })
      .catch((err) => {
        if (this._unmounted) return;
        console.error('MFA confirm failed:', err);
        this.setState({ isSubmitting: false, error: this.t('invalid') });
      });
  }

  handleCopy() {
    const text = this.state.backupCodes.join('\n');
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).then(() => {
        if (this._unmounted) return;
        this.setState({ copied: true });
        this._copyTimer = setTimeout(() => this.setState({ copied: false }), 2000);
      });
    }
  }

  handleCopySecret() {
    if (navigator.clipboard && this.state.otpSecret) {
      navigator.clipboard.writeText(this.state.otpSecret).then(() => {
        if (this._unmounted) return;
        this.setState({ secretCopied: true });
        this._secretTimer = setTimeout(() => this.setState({ secretCopied: false }), 2000);
      });
    }
  }

  handleFinish() {
    if (!this.state.acknowledged) return;
    this.props.onNext && this.props.onNext();
  }

  // --- Shared chrome ---------------------------------------------------------
  renderBadge(variant) {
    const map = {
      primary: 'bg-succession-primary-10 text-succession-primary',
      danger: 'bg-red-50 text-red-500',
    };
    const icon =
      variant === 'danger' ? (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75M6 10.5h12a1.5 1.5 0 0 1 1.5 1.5v6A1.5 1.5 0 0 1 18 19.5H6A1.5 1.5 0 0 1 4.5 18v-6A1.5 1.5 0 0 1 6 10.5z"
        />
      ) : (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 12.75 11.25 15 15 9.75M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z"
        />
      );
    return (
      <div className="flex justify-center mb-5">
        <span className={`inline-flex h-12 w-12 items-center justify-center rounded-full ${map[variant] || map.primary}`}>
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
            {icon}
          </svg>
        </span>
      </div>
    );
  }

  renderHeader(title, subtitle, variant) {
    return (
      <div className="mb-6">
        {this.renderBadge(variant)}
        <h2 className="text-xl font-bold text-center text-succession-text-primary">{title}</h2>
        {subtitle && (
          <p className="mt-2 text-sm text-center text-succession-text-secondary leading-relaxed">{subtitle}</p>
        )}
      </div>
    );
  }

  renderError(message) {
    if (!message) return null;
    return (
      <div className="mb-5 rounded-lg border border-red-200 bg-succession-error-bg px-4 py-3 text-sm text-succession-error">
        {message}
      </div>
    );
  }

  renderOtpBoxes() {
    const { totpCode, isSubmitting } = this.state;
    return (
      <div className="flex justify-between gap-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <input
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            ref={(el) => {
              this.otpRefs[i] = el;
            }}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={1}
            disabled={isSubmitting}
            value={totpCode[i] || ''}
            onChange={(e) => this.handleOtpChange(i, e.target.value)}
            onKeyDown={(e) => this.handleOtpKeyDown(i, e)}
            onPaste={this.handleOtpPaste}
            className="h-12 w-full min-w-0 rounded-lg border border-succession-border text-center text-lg font-semibold text-succession-text-primary focus:border-succession-primary focus:outline-none focus:ring-2 focus:ring-succession-primary-20"
          />
        ))}
      </div>
    );
  }

  renderPairing() {
    const { qrSvg, otpSecret, totpCode, isSubmitting, error, secretCopied } = this.state;
    const ready = totpCode.length === 6 && !isSubmitting;
    return (
      <form onSubmit={this.handleVerify}>
        {this.renderHeader(this.t('title'), this.t('subtitle'), 'primary')}
        {this.renderError(error)}

        {/* QR */}
        <div className="flex justify-center">
          <div className="rounded-xl border border-succession-border p-3">
            <div
              className="h-44 w-44 [&_svg]:h-full [&_svg]:w-full"
              // QR is a server-generated SVG (RQRCode); safe to inline.
              dangerouslySetInnerHTML={{ __html: qrSvg }}
            />
          </div>
        </div>

        {/* Manual setup key */}
        <p className="mt-5 text-center text-xs text-succession-text-secondary">{this.t('manual_entry')}</p>
        <div className="mt-2 flex items-center gap-2 rounded-lg border border-succession-border bg-succession-primary-10 px-3 py-2">
          <code className="flex-1 truncate font-mono text-sm tracking-wider text-succession-text-primary">
            {otpSecret}
          </code>
          <button
            type="button"
            onClick={this.handleCopySecret}
            className="shrink-0 text-succession-text-secondary hover:text-succession-primary focus:outline-none"
            aria-label={this.t('copy_secret')}
            title={secretCopied ? this.t('copied') : this.t('copy_secret')}
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.75 17.25v3a1.5 1.5 0 0 1-1.5 1.5h-9a1.5 1.5 0 0 1-1.5-1.5v-9a1.5 1.5 0 0 1 1.5-1.5h3m3.75-3h6a1.5 1.5 0 0 1 1.5 1.5v6a1.5 1.5 0 0 1-1.5 1.5h-6a1.5 1.5 0 0 1-1.5-1.5v-6a1.5 1.5 0 0 1 1.5-1.5z"
              />
            </svg>
          </button>
        </div>

        {/* Code entry */}
        <div className="mt-6">
          <label className="block text-sm font-medium text-succession-text-primary mb-2">{this.t('code_label')}</label>
          {this.renderOtpBoxes()}
          <p className="mt-2 text-xs text-center text-succession-text-secondary">{this.t('code_hint')}</p>
        </div>

        <button type="submit" disabled={!ready} className={ready ? PRIMARY_BTN : PRIMARY_BTN_DISABLED}>
          {isSubmitting ? this.t('verifying') : this.t('verify')}
        </button>
      </form>
    );
  }

  renderBackupCodes() {
    const { backupCodes, acknowledged, copied } = this.state;
    const downloadHref = `data:text/plain;charset=utf-8,${encodeURIComponent(backupCodes.join('\n'))}`;
    return (
      <div>
        {this.renderHeader(this.t('backup_title'), this.t('backup_body'), 'primary')}

        {/* Secure-storage notice */}
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
          <svg className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m0 3.75h.008M10.34 3.94 1.7 18.06A1.5 1.5 0 0 0 3 20.25h18a1.5 1.5 0 0 0 1.3-2.19L13.66 3.94a1.5 1.5 0 0 0-2.66 0z" />
          </svg>
          <span>{this.t('secure_note')}</span>
        </div>

        {/* Codes */}
        <ul className="mt-4 grid grid-cols-2 gap-2">
          {backupCodes.map((code, i) => (
            <li key={code} className="flex items-center gap-2 rounded-lg border border-succession-border bg-succession-primary-10 px-3 py-2">
              <span className="text-xs font-medium text-succession-text-secondary">{i + 1}</span>
              <code className="font-mono text-sm tracking-wider text-succession-text-primary">{code}</code>
            </li>
          ))}
        </ul>

        {/* Copy / download */}
        <div className="mt-3 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={this.handleCopy}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-succession-border bg-white px-3 py-2 text-sm font-medium text-succession-text-primary transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-succession-primary-20"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 17.25v3a1.5 1.5 0 0 1-1.5 1.5h-9a1.5 1.5 0 0 1-1.5-1.5v-9a1.5 1.5 0 0 1 1.5-1.5h3m3.75-3h6a1.5 1.5 0 0 1 1.5 1.5v6a1.5 1.5 0 0 1-1.5 1.5h-6a1.5 1.5 0 0 1-1.5-1.5v-6a1.5 1.5 0 0 1 1.5-1.5z" />
            </svg>
            {copied ? this.t('copied') : this.t('copy')}
          </button>
          <a
            href={downloadHref}
            download="backup-codes.txt"
            className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-succession-border bg-white px-3 py-2 text-sm font-medium text-succession-text-primary transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-succession-primary-20"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A1.5 1.5 0 0 0 4.5 20.25h15a1.5 1.5 0 0 0 1.5-1.5V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
            </svg>
            {this.t('download')}
          </a>
        </div>

        {/* Acknowledge + continue */}
        <label className="mt-5 flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(ev) => this.setState({ acknowledged: ev.target.checked })}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-succession-primary focus:ring-succession-primary-20"
          />
          <span className="text-sm text-succession-text-primary">{this.t('backup_ack')}</span>
        </label>
        <button
          type="button"
          onClick={this.handleFinish}
          disabled={!acknowledged}
          className={acknowledged ? PRIMARY_BTN : PRIMARY_BTN_DISABLED}
        >
          {this.t('finish')}
        </button>
      </div>
    );
  }

  renderLocked() {
    const { lockedUntil } = this.state;
    let until = null;
    if (lockedUntil) {
      const dt = new Date(lockedUntil);
      if (!Number.isNaN(dt.getTime())) until = dt.toLocaleTimeString();
    }
    return (
      <div>
        {this.renderHeader(this.t('locked_title'), this.t('locked_body'), 'danger')}
        {until && (
          <p className="text-sm text-center text-succession-text-secondary">{`${this.t('locked_until')} ${until}`}</p>
        )}
      </div>
    );
  }

  render() {
    const { phase } = this.state;

    let body;
    if (phase === 'loading') {
      body = <div className="text-center py-10 text-succession-text-secondary">…</div>;
    } else if (phase === 'pairing') {
      body = this.renderPairing();
    } else if (phase === 'backup_codes') {
      body = this.renderBackupCodes();
    } else if (phase === 'locked') {
      body = this.renderLocked();
    } else {
      body = (
        <div>
          {this.renderHeader(this.t('locked_title'), null, 'danger')}
          {this.renderError(this.state.error || this.t('setup_error'))}
        </div>
      );
    }

    return <div className="max-w-md mx-auto">{body}</div>;
  }
}

MfaEnrollmentStep.propTypes = {
  client: PropTypes.object,
  onNext: PropTypes.func,
  onBack: PropTypes.func,
  currentLanguage: PropTypes.string,
};

export default MfaEnrollmentStep;
